import { Injectable, OnDestroy } from "@angular/core";
import { GridDataResult } from "@progress/kendo-angular-grid";

import { RequestDelete } from "@myapp/modules/fwcore/models/messages";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { catchError, finalize, map, takeUntil, tap } from "rxjs/operators";
import { RoleTemplate } from "../../models";
import { RequestRoleTemplate, RequestRoleTemplateSet } from "../../models/messages";
import { AdminService } from "../admin.service";

@Injectable({
  providedIn: 'root'
})

export class RoleTemplateServiceGrid extends BehaviorSubject<GridDataResult> implements OnDestroy {

  /*-------------------------- public properties --------------------*/

  public loading: boolean = true;

  /*-------------------------- Internal properties  ------------------*/

  protected take: number = 5;

  protected destroy$ = new Subject();

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected lservice: AdminService) {

    const emptyDataResult = {
      data: [],
      total: 0
    }

    super(emptyDataResult);
  }

  /*----------------------------------- ng  --------------------------*/

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*-------------------------- public methods ------------------------*/

  query(req: RequestRoleTemplate) {

    this.fetch(req)
      .subscribe((x) => super.next(x));
  }

  queryAll(): Observable<GridDataResult> {

    const req: RequestRoleTemplate = {

    };

    return this.fetch(req);
  }

  fetch(req: RequestRoleTemplate): Observable<GridDataResult> {

    this.take = req.take ?? 0;

    return this.lservice.getRoleTemplate$(req)
      .pipe(
        map(
          (response) =>
            <GridDataResult>{
              data: response.data
              , total: response.total
            }
        )
        , finalize(() => (this.loading = false))
      );
  }

  save(data: RoleTemplate[]): Observable<boolean> {

    return this.saveApi(data)
      .pipe(
        map(modifiedData => {
          return this.syncModificationsToLocal(modifiedData);
        })
        , tap(x => { super.next(x); })
        , map(() => true) // Return true indicating successful save
        , catchError(() => of(false)) // Handle error and return false indicating unsuccessful save
      );
  }

  remove(data: RoleTemplate) {

    let req: RequestDelete = new RequestDelete();
    req.moduleId = data.moduleId;
    req.id = data.id!;

    this.lservice.removeRoleTemplate$(req)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success) {
          this.removeLocalItemById(data.id!);
        }
      });
  }

  /*--------------------------- internal functions  -------------------*/

  protected saveApi(data: RoleTemplate[]): Observable<RoleTemplate[]> {

    let req: RequestRoleTemplateSet = new RequestRoleTemplateSet();
    req.data = data;

    return this.lservice.setRoleTemplate$(req)
      .pipe(
        map(response => response.data!)
        , finalize(() => (this.loading = false))
      );

  }

  protected syncModificationsToLocal(modifiedItems: RoleTemplate[]): GridDataResult {

    let totalCount = this.value.total;

    let pos: number = 0;
    let item: RoleTemplate = {};
    let itemMod: RoleTemplate = {};

    let newData: RoleTemplate[] = [];

    // Create a copy of our array to manipulate and return
    for (pos = 0; pos < this.value.data.length; pos++) {
      newData.push(this.value.data[pos]);
    }

    // Get each of the modified items
    for (let j = modifiedItems.length - 1; j >= 0; j--) {

      itemMod = modifiedItems[j];
      let found = false;

      // See if they exist in our data already
      for (let i = newData.length - 1; i >= 0; i--) {

        item = newData[i];

        if (item.id == itemMod.id) {

          // match was found so substitute the modification
          newData.splice(i, 1, itemMod);
          found = true;
          break;
        }
      }

      if (found == false) {
        // Not found so insert the new item at the top of the list - user can see addition then
        newData.splice(0, 0, itemMod);
        totalCount += 1;

        // Remove items from the end of the array if we now overlap the page size due to the addition
        if (newData.length > this.take)
          newData.splice(this.take, 1);
      }
    }

    // Return the updated data array
    return { data: newData, total: totalCount };
  }

  protected removeLocalItemById(id: string) {

    this.value.data.forEach((item, index) => {
      if (item.id == id) {
        this.value.data.splice(index, 1);
        this.value.total -= 1;
      }
    });
  }

}
